/**
 * `@mail-sv/shared` の `labels.ja.ts` にまだ語彙が無い列挙の訳語。
 *
 * **原則は `labelOf()`（shared が唯一の出典）**。ここに置くのは、R0 で足した
 * 運営専用の表（`restore_runs` / `sla_credits` / `incidents` / `dr_drills` /
 * `ip_warmup_schedules` / `job_outbox`）の状態で、これらは契約者面には
 * 一切出ないため shared の語彙に載っていない。`labelOf()` は未知の値を
 * **生値のまま返す**ので、放っておくと画面に `awaiting_operator` と出る。
 *
 * shared 側へ移すときは、ここを消して `labelOf()` に一本化すること
 * （2 か所に訳語を置いたまま放置しない）。
 */
import type { Tone } from '@mail-sv/shared';

type LabelMap = Readonly<Record<string, string>>;
type ToneMap = Readonly<Record<string, Tone>>;

/* --------------------------------------------------------------- 復元要求 */

/** `restore_runs.status`（`packages/db/src/schema/infra.ts`）。 */
export const RESTORE_STATUS_LABEL: LabelMap = {
  requested: '要求受付',
  // 「自動実行の口が無い」ことを状態として持つ（黙って成功にしない）。
  awaiting_operator: '運営の実施待ち',
  approved: '承認済み',
  dispatched: 'ノードへ指示済み',
  running: '実行中',
  succeeded: '完了',
  failed: '失敗',
  canceled: '取り消し',
};

export const RESTORE_STATUS_TONE: ToneMap = {
  requested: 'info',
  awaiting_operator: 'warning',
  approved: 'info',
  dispatched: 'info',
  running: 'info',
  succeeded: 'success',
  failed: 'destructive',
  canceled: 'muted',
};

/** まだ人の手が要る（督促が続く）状態。一覧の既定フィルタに使う。 */
export const RESTORE_OPEN_STATUSES: readonly string[] = [
  'requested',
  'awaiting_operator',
  'approved',
  'dispatched',
  'running',
];

/* --------------------------------------------------------- SLA クレジット */

export const SLA_CREDIT_STATUS_LABEL: LabelMap = {
  proposed: '承認待ち',
  approved: '承認済み',
  rejected: '却下',
  applied: '請求書へ反映済み',
};

export const SLA_CREDIT_STATUS_TONE: ToneMap = {
  proposed: 'warning',
  approved: 'info',
  rejected: 'muted',
  applied: 'success',
};

/* ----------------------------------------------------------- インシデント */

export const INCIDENT_SEVERITY_LABEL: LabelMap = {
  sev1: 'Sev1 全面障害',
  sev2: 'Sev2 重度',
  sev3: 'Sev3 軽度',
  sev4: 'Sev4 情報',
};

export const INCIDENT_SEVERITY_TONE: ToneMap = {
  sev1: 'destructive',
  sev2: 'destructive',
  sev3: 'warning',
  sev4: 'info',
};

export const INCIDENT_STATUS_LABEL: LabelMap = {
  open: '対応中',
  mitigated: '暫定対応済み',
  resolved: '解消',
  postmortem: '事後分析中',
};

export const INCIDENT_STATUS_TONE: ToneMap = {
  open: 'destructive',
  mitigated: 'warning',
  resolved: 'success',
  postmortem: 'info',
};

/* ------------------------------------------------------------- 復旧演習 */

/**
 * `dr_drills.kind`。値の集合は `packages/db/migrations/0038_infra2.sql` の
 * CHECK 制約と API の zod（`apps/api/src/routes/ops/jobs.ts`）に一致させる。
 */
export const DR_DRILL_KIND_LABEL: LabelMap = {
  cp_restore: 'CP の復元',
  cp_pitr: 'CP のポイントインタイム復旧',
  node_total_loss: 'ノード全損からの再構築',
  mailbox_restore: 'メールボックス単位の復元',
  object_storage: 'オブジェクトストレージの復元',
};

export const DR_DRILL_OUTCOME_LABEL: LabelMap = {
  passed: '合格',
  partial: '一部未達',
  failed: '不合格',
};

export const DR_DRILL_OUTCOME_TONE: ToneMap = {
  passed: 'success',
  partial: 'warning',
  failed: 'destructive',
};

/* ------------------------------------------------------------- outbox */

export const OUTBOX_STATUS_LABEL: LabelMap = {
  pending: '未送出',
  sent: '送出済み',
  failed: '失敗（再試行中）',
  dead: '再試行を打ち切り',
};

export const OUTBOX_STATUS_TONE: ToneMap = {
  pending: 'warning',
  sent: 'success',
  failed: 'warning',
  dead: 'destructive',
};

/* --------------------------------------------------- IP ウォームアップ */

/** `warmup_status`（`packages/db/src/schema/_enums.ts`）。 */
export const WARMUP_STATUS_LABEL: LabelMap = {
  scheduled: '開始待ち',
  running: '進行中',
  completed: '完了',
  aborted: '中止',
};

export const WARMUP_STATUS_TONE: ToneMap = {
  scheduled: 'info',
  running: 'warning',
  completed: 'success',
  aborted: 'muted',
};

/* ----------------------------------------------- レピュテーション登録 */

/**
 * `reputation_programs.status`（`REPUTATION_PROGRAM_STATUS`）。
 *
 * **`not_registered` は「DB に行が無い」を表す合成値**で、API がカタログと
 * 突き合わせて必ず 1 行返す。「まだ何もしていない」ことを状態として画面に
 * 出さないと、登録漏れが永久に見えないままになる。
 */
export const REPUTATION_STATUS_LABEL: LabelMap = {
  not_registered: '未登録',
  pending: '申請中',
  registered: '登録済み',
  rejected: '却下',
  expired: '失効',
};

export const REPUTATION_STATUS_TONE: ToneMap = {
  not_registered: 'warning',
  pending: 'info',
  registered: 'success',
  rejected: 'destructive',
  expired: 'destructive',
};

/** `reputation_programs.scope`。ノード単位のものは台数ぶん登録が要る。 */
export const REPUTATION_SCOPE_LABEL: LabelMap = {
  service: 'サービス全体',
  node: 'ノードごと',
};

/* --------------------------------------------------------------- 使用量 */

/**
 * `usage_metric_key`（`packages/db/src/schema/_enums.ts` の `USAGE_METRIC_KEY`）。
 *
 * `labelOf()` はこれらを知らないので、ダッシュボードの使用量パネルには
 * **`ALIAS_COUNT` のような生値が大文字で並んでいた**（StatCard のラベルは
 * CSS で大文字化されるため、生値だと余計に「壊れている」ように見える）。
 * 値を足すときは `_enums.ts` の並びと突き合わせること。
 */
export const USAGE_METRIC_LABEL: LabelMap = {
  mailbox_count: 'メールボックス数',
  domain_count: 'ドメイン数',
  alias_count: 'エイリアス数',
  storage_bytes: '使用容量',
  outbound_messages: '送信通数',
  outbound_bytes: '送信バイト数',
  inbound_messages: '受信通数',
  inbound_rejected: '受信拒否',
  spam_blocked: 'スパム判定',
  virus_blocked: 'ウイルス判定',
  ai_input_tokens: 'AI 入力トークン',
  ai_output_tokens: 'AI 出力トークン',
  image_proxy_fetches: '画像プロキシ取得数',
  image_proxy_bytes: '画像プロキシ転送量',
};

/* --------------------------------------------------------------- 補助 */

/** 訳語表を引く。未知の値は生値のまま返す（`labelOf()` と同じ振る舞い）。 */
export function labelFrom(map: LabelMap, value: string | null | undefined): string {
  if (!value) return '—';
  return map[value] ?? value;
}

/** トーン表を引く。未知の値は `default`（`toneOf()` と同じ振る舞い）。 */
export function toneFrom(map: ToneMap, value: string | null | undefined): Tone {
  if (!value) return 'muted';
  return map[value] ?? 'default';
}

/** `<select>` の選択肢を訳語表から作る。 */
export function optionsFromLabels(map: LabelMap): Array<{ value: string; label: string }> {
  return Object.entries(map).map(([value, label]) => ({ value, label }));
}

/* ------------------------------------------------------------ 問い合わせ */

/**
 * `support_tickets.kind`（`packages/db/src/schema/security.ts`）。
 * 外部受付（abuse / security / legal）は組織に紐付かず、SLA も別建て。
 */
export const SUPPORT_KIND_LABEL: LabelMap = {
  support: 'サポート',
  abuse: '迷惑行為の通報',
  security: '脆弱性・セキュリティ',
  legal: '法的請求',
  privacy: '個人情報',
  billing: '請求',
};

/** `support_tickets.status`。`waiting_ops` が「こちらのボール」。 */
export const SUPPORT_STATUS_LABEL: LabelMap = {
  open: '受付',
  waiting_customer: '契約者の返信待ち',
  waiting_ops: '運営の対応待ち',
  resolved: '解決',
  closed: 'クローズ',
};

export const SUPPORT_STATUS_TONE: ToneMap = {
  open: 'warning',
  waiting_customer: 'info',
  waiting_ops: 'warning',
  resolved: 'success',
  closed: 'muted',
};

export const SUPPORT_PRIORITY_LABEL: LabelMap = {
  low: '低',
  normal: '通常',
  high: '高',
  urgent: '緊急',
};

export const SUPPORT_PRIORITY_TONE: ToneMap = {
  low: 'muted',
  normal: 'default',
  high: 'warning',
  urgent: 'destructive',
};

/** `support_tickets.source`。どこから入ってきた問い合わせか。 */
export const SUPPORT_SOURCE_LABEL: LabelMap = {
  portal: 'コントロールパネル',
  public: '公開窓口（abuse@ 等）',
  ops: '運営が代理起票',
};

/* --------------------------------------------------------- 権利行使要求 */

/**
 * `data_subject_requests.kind`。個人情報保護法の各請求権に対応する
 * （`apps/api/src/services/support.ts` の `DATA_SUBJECT_REQUEST_KINDS` が正）。
 */
export const DSR_KIND_LABEL: LabelMap = {
  access: '開示（33 条）',
  rectification: '訂正・追加・削除（34 条）',
  erasure: '利用停止・消去（35 条）',
  portability: '電磁的記録での提供',
  suspension: '第三者提供の停止（35 条）',
  third_party_record: '第三者提供記録の開示（33 条 5 項）',
};

export const DSR_STATUS_LABEL: LabelMap = {
  received: '受付',
  verifying: '本人確認中',
  in_progress: '対応中',
  completed: '完了',
  rejected: '拒否',
};

export const DSR_STATUS_TONE: ToneMap = {
  received: 'warning',
  verifying: 'warning',
  in_progress: 'info',
  completed: 'success',
  rejected: 'muted',
};

/* ------------------------------------------------------ メンテナンス告知 */

/**
 * `maintenance_windows.kind`。**`planned` だけが「事前告知が要る」種別**で、
 * それ以外は発生後の記録（稼働率の算定から外す根拠）として登録する。
 */
export const MAINTENANCE_KIND_LABEL: LabelMap = {
  planned: '計画メンテナンス',
  force_majeure: '不可抗力',
  customer_caused: '契約者起因',
  aup_enforcement: 'AUP に基づく停止',
};

export const MAINTENANCE_KIND_TONE: ToneMap = {
  planned: 'info',
  force_majeure: 'warning',
  customer_caused: 'muted',
  aup_enforcement: 'destructive',
};

/* ------------------------------------------------------------ 運営通知 */

export const NOTIFICATION_SEVERITY_LABEL: LabelMap = {
  info: '情報',
  warning: '警告',
  critical: '緊急',
};

export const NOTIFICATION_SEVERITY_TONE: ToneMap = {
  info: 'info',
  warning: 'warning',
  critical: 'destructive',
};

/* ------------------------------------------------------ ジョブの対象種別 */

/**
 * `provisioning_jobs.subject_type`（R81 の絞り込み用）。
 *
 * **DB では enum ではなく `text`**（`packages/db/src/schema/jobs.ts`）なので
 * 型からは値を導けない。ここに並べているのは
 * `grep -rh "subjectType: '"` で拾った**実際に enqueue されている 6 種**。
 * 一覧の絞り込みに使うだけなので、漏れがあっても
 * 「その選択肢が出ない」で済む（フィルタ自体は URL に任意の値を書ける）。
 * ジョブを足して新しい対象種別を使い始めたら、ここにも足すこと。
 */
export const JOB_SUBJECT_TYPE_LABEL: LabelMap = {
  organization: '契約組織',
  domain: 'ドメイン',
  mailbox: 'メールボックス',
  server_node: 'ノード',
  // `server_node` と `node` が両方使われている（enqueue 側の表記ゆれ）。
  // 片方だけ載せると、もう一方で積まれたジョブが選択肢から消える。
  node: 'ノード（旧表記）',
  tls_certificate: 'TLS 証明書',
};

/* ------------------------------------------------ break-glass（A321） */

/**
 * 本文閲覧の対象種別。
 *
 * `campaign` は自分では本文を持たず、紐づく成果物（HTML）へ落ちる
 * （API の `resolveTarget`）。「配信そのもの」と「その本文」が
 * 1:1 でないことを画面の語彙でも示しておく。
 */
export const BREAK_GLASS_TARGET_LABEL: LabelMap = {
  content_revision: '下書き（ブロック JSON）',
  content_artifact: '成果物（HTML）',
  campaign: '配信（本文は成果物）',
};
